import { FC, ReactNode } from 'react';
import type { StagedDateField } from 'types/well-workspace';
import { FieldGrid, fieldDate, type Field } from './field-grid';
import { StatusDot } from './status-dot';

export interface WellStatusValues {
  /** The stored status token — Epic 1 has no live status calculation. */
  status: string | null;
  permitDate: string | null;
  spudDate: string | null;
  rigReleaseDate: string | null;
  completionDate: string | null;
  firstProductionDate: string | null;
  lastProductionDate: string | null;
}

/** Names one row, so a host overriding a row's render says WHICH without a magic string. */
export type WellStatusRow = 'status' | StagedDateField;

/**
 * The rows, in the chronological order the mock lists them.
 *
 * One table rather than seven hand-written entries: the camelCase key, the snake_case key
 * the backend marks edits with, and the label used to be repeated per row, so a rename had
 * three places to miss. It also gives `renderValue` a real key to switch on.
 */
const ROWS: {
  row: WellStatusRow;
  /** As the backend names it in `editedFields` — jbuilder camelizes keys, not array values. */
  serverKey: string;
  label: string;
}[] = [
  { row: 'status', serverKey: 'status', label: 'SRP Well Status' },
  { row: 'permitDate', serverKey: 'permit_date', label: 'Permit Date' },
  { row: 'spudDate', serverKey: 'spud_date', label: 'Spud Date' },
  {
    row: 'rigReleaseDate',
    serverKey: 'rig_release_date',
    label: 'Rig Release Date',
  },
  {
    row: 'completionDate',
    serverKey: 'completion_date',
    label: 'Completion Date',
  },
  // First Production Date IS stageable on a well with no production — the operator
  // supplies it there, so it earns a pill like any other edited field. (On a well with
  // production it stays derived and never reaches `editedFields`, so the pill simply
  // never shows.)
  {
    row: 'firstProductionDate',
    serverKey: 'first_production_date',
    label: 'First Production Date',
  },
  // Last Production Date has no override column anywhere — always derived, never staged,
  // so it carries no pill and no host can make it editable. Moved here from Well Details
  // (UAT): the last date is part of the well's lifecycle, so it sits with the dates that
  // produced the status.
  {
    row: 'lastProductionDate',
    serverKey: 'last_production_date',
    label: 'Last Production Date',
  },
];

/**
 * Every row's read-only value, so one place decides how each is formatted.
 * @param {WellStatusRow} row Which row.
 * @param {WellStatusValues} well The well's values.
 * @return {ReactNode} The formatted value, or an em-dash when absent.
 */
const readOnlyValue = (
  row: WellStatusRow,
  well: WellStatusValues
): ReactNode => {
  if (row === 'status') {
    return well.status ? <StatusDot status={well.status} /> : '—';
  }

  return fieldDate(well[row]);
};

interface Props {
  well: WellStatusValues;
  /**
   * The staged fields that diverge from the canonical well, as the backend names them —
   * SNAKE_CASE (`spud_date`), because jbuilder camelizes keys but not array values. Each
   * one marks its field with `editedMark`. Omitted outside a workspace, where nothing is
   * staged and there is nothing to mark.
   */
  editedFields?: string[];
  /** What an edited field is marked with — the workspace's green "Edited" pill. */
  editedMark?: ReactNode;
  /**
   * Replaces a row's rendered value — how the workspace card makes rows editable in place.
   *
   * A render override rather than a bundle of editing props: an editor for these rows has
   * to know about staged wells, the first-production-date rule and a workspace mutation,
   * none of which belong in a component the Well page also renders. Returning undefined
   * keeps the read-only render, so a host can override some rows and not others.
   */
  renderValue?: (row: WellStatusRow, readOnly: ReactNode) => ReactNode;
}

/**
 * Well Status — the status and the lifecycle dates that produced it, in the
 * chronological order the mock lists them.
 *
 * These fields also appear on the Well Details card, which is the mock's own design
 * rather than an oversight: Well Status is the card Epic 2 makes editable, so it is
 * split out as the surface the edit and verdict controls attach to.
 * @param {Props} props The well's status and dates, any staged edits to mark, and an
 *   optional per-row render override.
 * @return {JSX.Element} The field grid.
 */
export const WellStatusFields: FC<Props> = ({
  well,
  editedFields,
  editedMark,
  renderValue,
}) => {
  const edited = new Set(editedFields ?? []);

  const fields: Field[] = ROWS.map(({ row, serverKey, label }) => {
    const readOnly = readOnlyValue(row, well);

    return {
      label,
      value: renderValue?.(row, readOnly) ?? readOnly,
      // A field is marked only when the caller supplied something to mark it with, so the
      // Well page's own use of this component renders exactly as it did before.
      labelSuffix: edited.has(serverKey) ? editedMark : undefined,
    };
  });

  return <FieldGrid fields={fields} />;
};
